/**
 * Asset Management Routes Configuration
 */

import React, { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate, Outlet } from 'react-router-dom';

// The sub-module's single screen. Dashboard.tsx is lazy-loaded from inside it,
// so it is not routed to directly.
const AssetManagementDashboard = lazy(() => import('../pages/AssetManagementDashboard'));
const AssetDetail = lazy(() => import('../pages/AssetDetail'));

// Reports
const AssetDataQuality = lazy(() => import('../pages/data-quality'));

// Depreciation run screen (Phase 2): preview -> confirm -> post.
const AssetDepreciation = lazy(() => import('../pages/depreciation'));

// Maintenance queue (Phase 3): what is due, and every work order.
const AssetWorkOrders = lazy(() => import('../pages/work-orders'));

// Disposal register (Phase 4 follow-up): module-wide, not per asset.
const DisposalRegister = lazy(() => import('../pages/disposals'));

// Bulk geo-tagging (Phase 5 addendum) - a CSV of code/lat/lng checked against the register.
const BulkGeoTagging = lazy(() => import('../pages/geo-import'));

// Encroachment Register (Phase 6) - every case in the module, not just one asset's.
const EncroachmentRegister = lazy(() => import('../pages/encroachments'));

// Inspections (Phase 7). The register, and the phone flow field staff actually use.
const InspectionRegister = lazy(() => import('../pages/inspections'));
const FieldInspection = lazy(() => import('../pages/inspections/field'));

// Valuations (Phase 8) - market, SRO, insured and book values with their history.
const ValuationRegister = lazy(() => import('../pages/valuations'));

// Scan & verify (Phase 9) - the QR scan and physical verification trail.
const ScanAndVerify = lazy(() => import('../pages/verify'));

// Masters
const AssetCategories = lazy(() => import('../masters/categories'));
const ModeOfAcquisitionList = lazy(() => import('../masters/mode-of-acquisition'));
const AssetTypeList = lazy(() => import('../masters/asset-type'));
const AssetActionTypesList = lazy(() => import('../masters/asset-action-types'));
const OwnershipTypeList = lazy(() => import('../masters/ownership-type'));
// masters/category-mapping, NOT masters/categories — this pointed at the Categories
// folder, so the "Category Mapping" menu item opened the Categories list and
// CategoryMappingList (the only screen that mounts MergeCategoriesModal) was unreachable.
const CategoryMapping = lazy(() => import('../masters/category-mapping'));
const SachivalayamMapping = lazy(() => import('../masters/sachivalayam-mapping'));

// Import routes from core modules
import { reportsRoutes } from '../../../../core/reports/routes';
import { setupRoutes } from '../../../../core/setup/routes';

// Simple pass-through layout for nested routes
const PassThroughLayout = () => <Outlet />;

export const assetManagementRoutes: RouteObject[] = [
  {
    // The one Asset Management screen. What used to be two sibling sub-modules —
    // "Dashboard" (KPI cards, activities, Constituency -> Sachivalayam grids) and
    // "Integrated" / "Geo Analytics" (heatmap + GIS layers) — are now a single
    // page: the map is a section of the dashboard rather than a second workspace.
    index: true,
    element: <AssetManagementDashboard />
  },
  {
    // Retired sub-module route. Old bookmarks, prefetch entries and the legacy
    // "Integrated" menu items land on the dashboard (whose map section is what
    // they were after) rather than a 404.
    path: 'integrated',
    element: <Navigate to="/app/maud-erp/asset-management" replace />
  },
  // The per-ULB and per-ward dashboards are gone: assets are organised strictly
  // UDA -> Constituency -> Mandal -> Panchayat -> Sachivalayam. Old bookmarks and
  // stale tabs land on the UDA dashboard rather than a 404.
  {
    path: 'ulb/:ulbId',
    element: <Navigate to="/app/maud-erp/asset-management" replace />
  },
  {
    path: 'ward/:wardId',
    element: <Navigate to="/app/maud-erp/asset-management" replace />
  },
  {
    path: 'categories',
    element: <AssetCategories />
  },
  {
    path: 'mode-of-acquisition',
    element: <ModeOfAcquisitionList />
  },
  {
    path: 'asset-type',
    element: <AssetTypeList />
  },
  {
    path: 'asset-action-types',
    element: <AssetActionTypesList />
  },
  {
    path: 'ownership-type',
    element: <OwnershipTypeList />
  },
  {
    path: 'category-mapping',
    element: <CategoryMapping />
  },
  {
    path: 'sachivalayam-mapping',
    element: <SachivalayamMapping />
  },
  {
    // Asset Data Quality (discrepancy) report — per-ULB issue counts. MUST stay
    // before the ':assetId' route below, else it is matched as an asset id
    // ("Asset Not Found").
    path: 'data-quality',
    element: <AssetDataQuality />
  },
  {
    // Same rule as data-quality above: this must stay before ':assetId', or the word
    // "depreciation" is matched as an asset id.
    path: 'depreciation',
    element: <AssetDepreciation />
  },
  {
    // Before ':assetId', for the same reason as the screens above.
    path: 'work-orders',
    element: <AssetWorkOrders />
  },
  {
    // Before ':assetId', like the screens above.
    path: 'disposals',
    element: <DisposalRegister />
  },
  {
    // Before ':assetId', like the screens above.
    path: 'geo-import',
    element: <BulkGeoTagging />
  },
  {
    // Before ':assetId', like the screens above.
    path: 'encroachments',
    element: <EncroachmentRegister />
  },
  {
    // Before ':assetId', like the screens above. The field flow is a child path so
    // that 'inspections' itself is not shadowed by it.
    path: 'inspections',
    element: <InspectionRegister />
  },
  {
    path: 'inspections/field',
    element: <FieldInspection />
  },
  {
    // Before ':assetId', like the screens above.
    path: 'valuations',
    element: <ValuationRegister />
  },
  {
    // Before ':assetId', like the screens above.
    path: 'verify',
    element: <ScanAndVerify />
  },
  {
    path: 'reports',
    element: <PassThroughLayout />,
    children: reportsRoutes
  },
  {
    path: 'setup',
    element: <PassThroughLayout />,
    children: setupRoutes
  },
  {
    path: ':assetId',
    element: <AssetDetail />
  }
];

export default assetManagementRoutes;
